import { useScrubberDrag } from '@src/hooks/useScrubberDrag';
import { SAFE_ZONE_STRINGS } from '@src/safe-zones/strings';
import { FullScreenIconAlt } from '@src/svgs/FullScreenIcon';
import GearIcon from '@src/svgs/GearIcon';
import { MuxPauseIcon } from '@src/svgs/MuxPauseIcon';
import { MuxPlayIcon } from '@src/svgs/MuxPlayIcon';
import SpeakerIcon from '@src/svgs/SpeakerIcon';
import SpeakerMutedIcon from '@src/svgs/SpeakerMutedIcon';
import YouTubeMiniplayerIcon from '@src/svgs/YouTubeMiniplayerIcon';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import { formatPlaybackTime } from '../formatPlaybackTime';
import { useChromeFontSize } from '../useChromeFontSize';
import { useCursorHover } from './sharedChrome';

import type { SafeZoneProps } from '@src/safe-zones/types';

const COPY = SAFE_ZONE_STRINGS.overlays.xFeedPost;

/**
 * X Feed Post overlay — paints just the in-video player chrome an X viewer
 * sees when they tap into an autoplay video card. Bare state shows nothing;
 * toggling `Player Controls` paints the bottom scrubber + controls bar;
 * toggling `Captions` paints a placeholder caption block in the lower-third
 * (anchored above the controls bar when controls are on, above the frame
 * edge when controls are off).
 *
 * Controls-on layout (matches X's modern in-card player):
 *
 *   ┌──────────────────────────────────────────────┐
 *   │                                              │
 *   │                                  ┌─────────┐ │ ← caption block (when on)
 *   │                                  │ captions│ │
 *   │                                  └─────────┘ │
 *   │                                              │
 *   │ ████████████░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░  │ ← scrubber (real)
 *   │ (⏸)                  0:11 / 31:10  🔇 ⚙ 🗗 ⛶  │ ← controls row
 *   └──────────────────────────────────────────────┘
 *
 * The play/pause button is a **circular translucent pill** on the far left;
 * time + right cluster (mute, settings, picture-in-picture, fullscreen) sit
 * together on the right with the spacer between them. On hover, a small
 * "Pause" / "Play" tooltip floats just above the circular button — matches
 * the affordance X's real player shows.
 *
 * Scaling: chrome is sized in `em` against a single base font-size that
 * scales with the video rect. The reference rect is **510×309**, where the
 * base font-size resolves to `10px`. Matches LinkedIn Feed Post's reference
 * so the two "in-video player chrome only" overlays read at the same density.
 *
 * Two control surfaces flip `showCaptions`: the toolbar pill (primary) and
 * the in-bar `CC` button (mirrors what an X viewer would click — X paints a
 * top-level CC affordance in the bottom controls row, so the overlay
 * mirrors that). Both fire `onToggleVariant('showCaptions')`. Mute,
 * settings, picture-in-picture, and fullscreen are decorative; only
 * play/pause, the scrubber, and the `CC` button drive renderer / variant
 * state.
 */
const REFERENCE_WIDTH = 510;
const REFERENCE_HEIGHT = 309;
const REFERENCE_FONT_PX = 10;
// Floor scale at 0.6 so chrome never collapses below readable size on tiny
// preview rects; ceil at 3 so it doesn't blow up if mounted full-window.
const SCALE_MIN = 0.6;
const SCALE_MAX = 3;

export const XFeedPostOverlay: React.FC<SafeZoneProps> = ({
  rect,
  variants,
  onToggleVariant,
  playback,
}) => {
  const fontPx = useChromeFontSize(rect, {
    referenceWidth: REFERENCE_WIDTH,
    referenceHeight: REFERENCE_HEIGHT,
    referenceFontPx: REFERENCE_FONT_PX,
    scaleMin: SCALE_MIN,
    scaleMax: SCALE_MAX,
  });

  const showControls = !!variants.showPlayControls;
  const showCaptions = !!variants.showCaptions;

  const isPlaying = playback?.isPlaying ?? false;
  const duration = playback?.duration ?? 0;
  const isReady = playback?.isReady ?? false;

  // Real scrubber drag/seek state — same hook the LinkedIn overlays use.
  const {
    scrubberRef,
    displayTime,
    progressPct,
    onMouseDown: onScrubberMouseDown,
  } = useScrubberDrag(playback);

  const togglePlayPause = () => {
    if (!playback || !isReady) return;
    if (isPlaying) playback.pause();
    else playback.play();
  };

  // Mute is visual-only today — the renderer has no volume API, so the
  // button swaps the speaker icon and flips a local boolean without changing
  // playback audio. Wired structurally for a future `playback.setVolume(0)`.
  const [muted, setMuted] = useState(false);

  // The chrome stays `pointer-events: none` so clicks pass through to the
  // underlying renderer; CSS `:hover` therefore won't fire reliably on the
  // play button. `useCursorHover` watches window mousemove against the
  // button's bounding rect — drives the dim→bright transition and the
  // "Pause" / "Play" tooltip.
  const playBtnRef = useRef<HTMLButtonElement | null>(null);
  const playHovered = useCursorHover(playBtnRef, showControls);

  const playAriaLabel = isPlaying ? COPY.aria.pause : COPY.aria.play;

  // Hover state for the CC button — drives the "Hide captions" / "Show
  // captions" floating tooltip. The button itself lives in
  // `pointer-events: auto`, so a plain `:hover` would work for any
  // CSS-driven feedback; the tooltip uses React state to keep the rendering
  // path consistent with the play button's tooltip.
  const [ccHovered, setCcHovered] = useState(false);
  const ccTooltip = showCaptions ? COPY.ccTooltip.hide : COPY.ccTooltip.show;

  return (
    <Frame
      data-testid="x-feed-post-overlay"
      style={{ fontSize: `${fontPx}px` }}
    >
      {/* Caption — always mounted, fades in/out via opacity */}
      <CaptionAnchor $controlsOn={showControls} $visible={showCaptions}>
        <CaptionBox>{COPY.captionPlaceholder}</CaptionBox>
      </CaptionAnchor>

      {showControls && (
        <ControlsBlock>
          <Scrubber
            ref={scrubberRef}
            role="slider"
            aria-label={COPY.aria.seek}
            aria-valuemin={0}
            aria-valuemax={Math.max(0, duration)}
            aria-valuenow={Math.max(0, displayTime)}
            onMouseDown={onScrubberMouseDown}
            $interactive={!!playback && isReady && duration > 0}
          >
            <ScrubberFill style={{ width: `${progressPct}%` }} />
          </Scrubber>

          <ControlsRow>
            <PlayBtnWrap>
              {playHovered && <ChromeTooltip>{playAriaLabel}</ChromeTooltip>}
              <CircularPlayBtn
                ref={playBtnRef}
                type="button"
                onClick={togglePlayPause}
                aria-label={playAriaLabel}
                aria-pressed={isPlaying}
                disabled={!playback || !isReady}
                $hovered={playHovered}
              >
                {isPlaying ? (
                  <MuxPauseIcon width="1.5em" height="1.5em" />
                ) : (
                  <MuxPlayIcon width="1.5em" height="1.5em" />
                )}
              </CircularPlayBtn>
            </PlayBtnWrap>

            <Spacer />

            <Time>
              {formatPlaybackTime(displayTime)} / {formatPlaybackTime(duration)}
            </Time>

            <CCBtnWrap
              onMouseEnter={() => setCcHovered(true)}
              onMouseLeave={() => setCcHovered(false)}
            >
              {ccHovered && <ChromeTooltip>{ccTooltip}</ChromeTooltip>}
              <CCBtn
                type="button"
                $active={showCaptions}
                onClick={() => onToggleVariant?.('showCaptions')}
                aria-pressed={showCaptions}
                aria-label={COPY.aria.toggleCaptions}
              >
                CC
              </CCBtn>
            </CCBtnWrap>

            <IconBtn
              type="button"
              onClick={() => setMuted(m => !m)}
              aria-label={muted ? COPY.aria.unmute : COPY.aria.mute}
              aria-pressed={muted}
            >
              {muted ? (
                <SpeakerMutedIcon
                  width="1.6em"
                  height="1.6em"
                  fillColor="#ffffff"
                  strokeColor="#ffffff"
                />
              ) : (
                <SpeakerIcon
                  width="1.6em"
                  height="1.6em"
                  fillColor="#ffffff"
                  strokeColor="#ffffff"
                />
              )}
            </IconBtn>

            {/* Settings / PiP / Fullscreen are decorative — they paint
                hover feedback so the chrome reads as a real player, but
                clicking does nothing. `aria-disabled` + `tabIndex={-1}`
                keep them out of the keyboard tab order and tell AT they're
                not actionable. */}
            <IconBtn
              type="button"
              aria-label={COPY.aria.settings}
              aria-disabled="true"
              tabIndex={-1}
            >
              <GearIcon width="1.4em" height="1.4em" color="#ffffff" />
            </IconBtn>

            {/* `YouTubeMiniplayerIcon` is the generic PiP glyph (outer
                rounded rect with smaller filled rect in the lower-right);
                its shape matches the X player's picture-in-picture button
                even though the icon name is YouTube-flavored. Reusing it
                avoids carrying a duplicate SVG. */}
            <IconBtn
              type="button"
              aria-label={COPY.aria.pictureInPicture}
              aria-disabled="true"
              tabIndex={-1}
            >
              <YouTubeMiniplayerIcon
                width="1.4em"
                height="1.12em"
                strokeColor="#ffffff"
              />
            </IconBtn>

            <IconBtn
              type="button"
              aria-label={COPY.aria.fullscreen}
              aria-disabled="true"
              tabIndex={-1}
            >
              <FullScreenIconAlt fill="#ffffff" />
            </IconBtn>
          </ControlsRow>
        </ControlsBlock>
      )}
    </Frame>
  );
};

// ---------------------------------------------------------------------------
// Styles — all dimensions in `em` (1em = 10px @ 510×309 reference).
// ---------------------------------------------------------------------------

const Frame = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', 'TwitterChirp', Helvetica,
    Arial, sans-serif;
  line-height: 1;
  color: #ffffff;
`;

// Caption position container. Tracks `$controlsOn` so the caption slides
// between two bottom offsets when the controls bar appears / disappears:
//   - controls on  → bottom: 4.4em (sits clear of the scrubber)
//   - controls off → bottom: 0.75em (sits near the frame edge)
// Animates the bottom value so the caption feels like it's stepping up to
// make room for chrome.
const CaptionAnchor = styled.div<{ $controlsOn: boolean; $visible: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: ${({ $controlsOn }) => ($controlsOn ? '4.4em' : '0.75em')};
  display: flex;
  justify-content: center;
  pointer-events: none;
  padding: 0 0.4em;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition:
    bottom 200ms ease-out,
    opacity 200ms ease-out;
`;

// Caption block — single unified translucent rectangle wrapping the whole
// caption (not per-line pills). Styled identically to the Facebook Feed
// Post overlay so the two read as one consistent caption family across
// platforms: subtle ~50% black wash, ~1.3em type, gentle radius, capped at
// 80% of the rect width so long lines wrap rather than touching the edges.
const CaptionBox = styled.div`
  display: inline-block;
  color: #ffffff;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 0.15em;
  padding: 0.4em 0.7em;
  font-size: 1.3em;
  font-weight: 500;
  letter-spacing: 0.005em;
  line-height: 1.4;
  text-align: center;
  max-width: 80%;
`;

// Bottom chrome block — owns both the scrubber and the controls row so the
// two paint as a single unit anchored to the rect bottom. Sits on a thin
// gradient that fades up into the video so the controls are legible without
// hard-cropping the imagery behind them.
const ControlsBlock = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 4.4em;
  padding: 0.4em 0.9em 0.5em;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.7) 0%,
    rgba(0, 0, 0, 0.4) 60%,
    rgba(0, 0, 0, 0) 100%
  );
  color: #ffffff;
  display: flex;
  flex-direction: column;
`;

// Full-width scrubber rail at the top of the controls block. Opts in to
// `pointer-events: auto` only when there's a real playback bridge to drive
// (matches LinkedIn Feed Post). X paints the scrubber as a flat fill line
// rather than a thumbed rail at this scale, so no thumb dot is rendered.
const Scrubber = styled.div<{ $interactive: boolean }>`
  height: 0.25em;
  background: rgba(255, 255, 255, 0.35);
  border-radius: 0.15em;
  position: relative;
  pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
`;

const ScrubberFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: #ffffff;
  border-radius: 0.15em;
`;

// Controls row — pause on the far left, time + right cluster on the far
// right with the spacer between. Bumping `font-size` here scales every
// child (icons, text, hit targets — all sized in `em`) without affecting
// the bar's outer height.
const ControlsRow = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.9em;
  font-size: 1.1em;
  padding-top: 0.5em;
`;

// Positioning wrapper for the circular play button + its hover tooltip.
// The tooltip floats above the button without affecting the row's vertical
// rhythm.
const PlayBtnWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
`;

// Translucent dark pill containing the play/pause glyph. Re-enables
// `pointer-events: auto` on itself so clicks toggle real play/pause; the
// rest of the overlay stays click-through. Hover state is driven by a JS
// effect in the component (the parent chrome is `pointer-events: none` so
// CSS `:hover` wouldn't fire reliably here).
const CircularPlayBtn = styled.button<{ $hovered: boolean }>`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.6em;
  height: 2.6em;
  border-radius: 50%;
  background: ${({ $hovered }) =>
    $hovered ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.55)'};
  color: #ffffff;
  flex: 0 0 auto;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  transition:
    background-color 150ms ease-out,
    opacity 150ms ease-out;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

// Small floating label above a controls-bar button — surfaces "Pause" /
// "Play" / "Show captions" / "Hide captions" while the cursor is over the
// button. Mirrors the affordance X's real player paints.
const ChromeTooltip = styled.span`
  position: absolute;
  bottom: calc(100% + 0.45em);
  left: 50%;
  transform: translateX(-50%);
  padding: 0.25em 0.6em;
  background: rgba(0, 0, 0, 0.85);
  border-radius: 0.3em;
  font-size: 0.85em;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
`;

const Time = styled.span`
  font-size: 1.05em;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  flex: 0 0 auto;
`;

// Pushes the time + right cluster to the far edge of the controls row,
// leaving the circular pause button alone on the left.
const Spacer = styled.div`
  flex: 1 1 auto;
`;

// Shared style for the four right-cluster buttons (mute, settings,
// picture-in-picture, fullscreen). They all opt into `pointer-events: auto`
// so the cursor turns into a pointer and CSS `:hover` works — no
// cursor-tracking JS needed. Only mute has an active `onClick` wire; the
// others are decorative <button> elements with aria labels for screen
// readers.
const IconBtn = styled.button`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.8em;
  height: 1.8em;
  color: #ffffff;
  flex: 0 0 auto;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  opacity: 0.85;
  transition: opacity 150ms ease-out;

  &:hover {
    opacity: 1;
  }
`;

// Positioning wrapper for the CC button + its hover tooltip. The wrapper
// itself stays `pointer-events: none` so only the inner button accepts the
// cursor; mouseenter/mouseleave on the wrapper drive the tooltip state.
const CCBtnWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
`;

// "CC" toggle that mirrors X's controls-bar caption button. Active state
// matches X's filled treatment (white pill, black text) so the on/off state
// is unambiguous; inactive uses a thin outline at reduced opacity to read
// as available-but-off.
const CCBtn = styled.button<{ $active: boolean }>`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.7em;
  height: 1.25em;
  padding: 0 0.35em;
  border-radius: 0.25em;
  border: 1px solid #ffffff;
  background: ${({ $active }) => ($active ? '#ffffff' : 'transparent')};
  color: ${({ $active }) => ($active ? '#000000' : '#ffffff')};
  font-family: inherit;
  font-size: 0.9em;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1;
  cursor: pointer;
  opacity: ${({ $active }) => ($active ? 1 : 0.85)};
  transition:
    background-color 150ms ease-out,
    color 150ms ease-out,
    opacity 150ms ease-out;

  &:hover {
    opacity: 1;
  }
`;
